import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FloatingShapeComponent } from '@catch/general-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { ScreenService } from '@catch/services';
import confetti from 'canvas-confetti';
import { fadeInOut } from '../../../../assets/animations/animations';
import { JobboardAdapter } from '../../../adapters/jobboard.adapter';
import { ColorService } from '../../../services/color.service';

enum PhoneCallStartOutcome {
  STARTED = 'started',
  NO_MISSING_DATA = 'no_missing_data',
}

const PHONE_CALL_NONCE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

@Component({
  selector: 'app-start-phone-call',
  templateUrl: './start-phone-call.component.html',
  styleUrls: ['./start-phone-call.component.scss'],
  animations: [fadeInOut],
  imports: [FloatingShapeComponent, TranslocoPipe],
})
export class StartPhoneCallComponent implements OnInit {
  private readonly colorService = inject(ColorService);
  private readonly jobboardAdapter = inject(JobboardAdapter);
  private readonly route = inject(ActivatedRoute);
  public readonly screenService = inject(ScreenService);

  @ViewChild('confettiTrigger') confettiTrigger: ElementRef;

  state: string;

  /**
   * i18n key suffix for the current non-success state.
   *
   * @returns Transloco leaf under start-phone-call
   */
  get resultKey(): 'no-missing-data' | 'failed' {
    return this.state === 'no-missing-data' ? 'no-missing-data' : 'failed';
  }

  /**
   * @returns Title transloco key for the current result card
   */
  get resultTitleKey(): string {
    return `jobboard.views.start-phone-call.${this.resultKey}.title`;
  }

  /**
   * @returns Body transloco key for the current result card
   */
  get resultTextKey(): string {
    return `jobboard.views.start-phone-call.${this.resultKey}.text`;
  }

  ngOnInit(): void {
    const nonce: string = this.route.snapshot.queryParams['nonce'];
    if (nonce && PHONE_CALL_NONCE.test(nonce)) {
      this.jobboardAdapter.startPhoneCall(nonce).subscribe({
        next: (response: { outcome?: string }) => {
          this.state =
            response?.outcome === PhoneCallStartOutcome.NO_MISSING_DATA
              ? 'no-missing-data'
              : 'success';
          if (this.state === 'success') {
            this.showConfetti();
          }
        },
        error: () => {
          this.state = 'failed';
        },
      });
    } else {
      this.state = 'failed';
    }
  }

  showConfetti(): void {
    confetti({
      particleCount: 50,
      spread: 70,
      origin: { y: 0.6 },
    });
  }

  get correctedBackgroundColor(): string {
    if (this.colorService.background) {
      if (
        this.colorService.background.startsWith('rgb') &&
        !this.colorService.background.startsWith('rgba')
      ) {
        return (
          this.colorService.background.substring(
            0,
            this.colorService.background.length - 2
          ) + ', 0.133333)'
        );
      } else if (
        this.colorService.background.startsWith('#') &&
        this.colorService.background.length <= 7
      ) {
        return this.colorService.background + '22';
      } else {
        return this.colorService.background;
      }
    }
    return '';
  }
}
